<template>
    <v-container fluid>
        <!-- Tab Navigation -->
        <v-tabs v-model="selectedTabIndex" @change="updateTab">
            <v-tab v-for="tab in tabs" :key="tab.value">{{ tab.text }}</v-tab>
        </v-tabs>

        <v-toolbar flat>
            <v-toolbar-title><b>Subtitle Ingest Dashboard</b></v-toolbar-title>
            <v-spacer></v-spacer>


            <!-- Timezone Toggle Button -->
            <v-chip color="primary" outlined @click="toggleTimezone">
                <span>Timezone :</span><b style="margin-left: 4px">{{ currentTimezone.name }}</b>
            </v-chip>

            <!-- Export Button -->
            <v-btn @click="exportToExcel" icon color="darkgrey">
                <v-icon>mdi-file-download</v-icon>
            </v-btn>

            <!-- Filter Select -->
            <v-select
                class="filter-select"
                v-model="selectedFilter"
                :items="filterOptions"
                label="Search by"
                solo
                style="max-width: 150px; margin-bottom: 0px;"
            ></v-select>

            <!-- General Text Filter -->
            <template v-if="selectedFilter === 'episode_title'">
                <v-text-field
                    class="search-field"
                    v-model="search"
                    label="Search"
                    append-icon="mdi-magnify"
                    single-line
                    hide-details
                    style="max-width: 400px;"
                    @input="updateSearch"
                ></v-text-field>
            </template>

            <!-- CP, TA, Category Filter -->
            <template v-if="selectedFilter === 'cp' || selectedFilter === 'english_ta' || selectedFilter === 'category'">
                <v-select
                    class="search-field"
                    v-model="search"
                    :items="dropdownOptions"
                    label="Select"
                    solo
                    hide-details
                    @input="updateSearch"
                ></v-select>
            </template>

            <!-- Date Filter -->
            <template v-if="selectedFilter === 'on_air_date' || selectedFilter.includes('ingested_date') || selectedFilter.includes('qc_date')">
                <v-menu
                    ref="dateMenu"
                    v-model="menu"
                    :close-on-content-click="false"
                    :nudge-right="40"
                    transition="scale-transition"
                    offset-y
                    min-width="auto"
                >
                    <template v-slot:activator="{ on, attrs }">
                        <v-text-field
                            v-model="dateRangeText"
                            label="Select Date Range"
                            append-icon="mdi-calendar"
                            readonly
                            v-bind="attrs"
                            v-on="on"
                        ></v-text-field>
                    </template>

                    <!-- Date Picker -->
                    <v-date-picker v-model="selectedDates" range scrollable @change="updateDateRange">
                        <v-spacer></v-spacer>
                        <v-btn text color="primary" @click="menu = false">OK</v-btn>
                    </v-date-picker>
                </v-menu>
            </template>
        </v-toolbar>

        <!-- Data Table -->
        <div style="width: 100%; overflow-x:auto;" class="scrollable-table">
            <v-data-table 
                ref="dataTable"
                :headers="tabFilteredHeaders" 
                :items="tabFilteredData" 
                :items-per-page="itemsPerPage" 
                :page.sync="page"
                class="custom-table">
                <template v-slot:empty>
                    <v-alert>No data available</v-alert>
                </template>
            </v-data-table>
        </div>
    </v-container>
</template>

<script>
import axios from 'axios';
import * as XLSX from 'xlsx'; // xlsx 라이브러리 임포트

export default {
    data() {
        return {
            loading: true,
            page: 1,
            itemsPerPage: 10,
            data: [],
            allData: [], // 전체 데이터
            search: '',
            selectedFilter: 'episode_title', // 기본 검색 필터
            selectedDates: [], // Date Picker에서 선택된 날짜 범위
            dateRangeText: '', // 선택된 날짜 범위 텍스트
            menu: false, // 달력 메뉴 상태
            timezones: [
                { name: 'KST (UTC+09:00)', offset: 9 * 60 * 60 * 1000 },
                { name: 'BRT (UTC-03:00)', offset: -3 * 60 * 60 * 1000 },
                { name: 'CST (UTC-06:00)', offset: -6 * 60 * 60 * 1000 },
                { name: 'ICT (UTC+07:00)', offset: 7 * 60 * 60 * 1000 },
                { name: 'CST (UTC+08:00)', offset: 8 * 60 * 60 * 1000 },
            ],
            timezoneIndex: 0,
            selectedTabIndex: 0,
            selectedTab: 'Master', // Default tab is Master
            tabs: [
                { text: 'Master', value: 'Master' },
                { text: 'Portuguese', value: 'Portuguese' },
                { text: 'Spanish', value: 'Spanish' },
                { text: 'French', value: 'French' },
                { text: 'German', value: 'German' },
                { text: 'Italian', value: 'Italian' },
                { text: 'Vietnamese', value: 'Vietnamese' },
                { text: 'Chinese', value: 'Chinese' },
                { text: 'Old', value: 'Old' }
            ],
            dropdownOptions: [], // CP 또는 TA, Category 필드에 대한 옵션 리스트
            headers: [
                { text: 'CP', value: 'cp' }, 
                { text: 'TA', value: 'english_ta' }, 
                { text: 'Category', value: 'category' },
                { text: 'Title', value: 'episode_title' },
                { text: 'DT (sec)', value: 'duration' },
                { text: 'DT (min)', value: 'durationMin' }, 
                { text: 'Min', value: 'min' },
                { text: 'On Air Date', value: 'on_air_date' }, 
                { text: 'English', value: 'english_ingested_date', width: '165px', align: 'center' },
                { text: 'English CC', value: 'english_cc_ingested_date', width: '165px', align: 'center' },
                { text: 'Portuguese', value: 'portuguese_ingested_date', width: '165px', align: 'center' }, 
                { text: 'Portuguese QC', value: 'portuguese_qc_date', width: '165px', align: 'center' },
                { text: 'Spanish', value: 'spanish_ingested_date', width: '165px', align: 'center' },
                { text: 'Spanish QC', value: 'spanish_qc_date', width: '165px', align: 'center' },
                { text: 'Portuguese AI', value: 'portuguese_ai_ingested_date', width: '165px', align: 'center' },
                { text: 'Spanish AI', value: 'spanish_ai_ingested_date', width: '165px', align: 'center' },
                { text: 'French AI', value: 'french_ai_ingested_date', width: '165px', align: 'center' },
                { text: 'German AI', value: 'german_ai_ingested_date', width: '165px', align: 'center' },
                { text: 'Italian AI', value: 'italian_ai_ingested_date', width: '165px', align: 'center' },
                { text: 'Vietnamese', value: 'vietnamese_ingested_date', width: '165px', align: 'center' },
                { text: 'Chinese', value: 'chinese_ingested_date', width: '165px', align: 'center' },
                { text: 'Chinese QC', value: 'chinese_qc_date', width: '165px', align: 'center' }, 
            ],
            tabFilteredHeaders: [], // 필터링된 헤더
            tabFilteredData: [] // 필터링된 데이터
        };
    },
    watch: {
        selectedFilter(newFilter) {
            if (newFilter === 'cp' || newFilter === 'english_ta' || newFilter === 'category') {
                this.setDropdownOptions();
            }
        },
        selectedTab() {
            this.updateFilteredData();
        },
        timezoneIndex() {
            this.updateFilteredData();
        }
    },
    computed: { 
        totalPages() {
            // 필터링된 데이터 길이에 따라 페이지 수를 계산
            return Math.ceil(this.filteredTitleData.length / this.itemsPerPage);
        },
        currentTimezone() {
            return this.timezones[this.timezoneIndex];
        },
        titleData() {
            return this.data.map(item => ({
                ...item,
                durationMin: item.duration ? this.formatDuration(item.duration) : '',
                min: item.duration ? Math.ceil(item.duration / 60) : '',
                on_air_date: item.on_air_date ? this.convertDateToTimezone(item.on_air_date) : '',
                english_ingested_date: item.english_ingested_date ? this.formatIngestedDates(item.english_ingested_date) : '',
                english_cc_ingested_date: item.english_cc_ingested_date ? this.formatIngestedDates(item.english_cc_ingested_date) : '',
                portuguese_ingested_date: item.portuguese_ingested_date ? this.formatIngestedDates(item.portuguese_ingested_date) : '',
                portuguese_qc_date: item.portuguese_qc_date ? this.formatIngestedDates(item.portuguese_qc_date) : '',
                spanish_ingested_date: item.spanish_ingested_date ? this.formatIngestedDates(item.spanish_ingested_date) : '',
                spanish_qc_date: item.spanish_qc_date ? this.formatIngestedDates(item.spanish_qc_date) : '',
                portuguese_ai_ingested_date: item.portuguese_ai_ingested_date ? this.formatIngestedDates(item.portuguese_ai_ingested_date) : '',

                spanish_ai_ingested_date: item.spanish_ai_ingested_date ? this.formatIngestedDates(item.spanish_ai_ingested_date) : '',
                french_ai_ingested_date: item.french_ai_ingested_date ? this.formatIngestedDates(item.french_ai_ingested_date) : '',
                german_ai_ingested_date: item.german_ai_ingested_date ? this.formatIngestedDates(item.german_ai_ingested_date) : '',
                italian_ai_ingested_date: item.italian_ai_ingested_date ? this.formatIngestedDates(item.italian_ai_ingested_date) : '',
                vietnamese_ingested_date: item.vietnamese_ingested_date ? this.formatIngestedDates(item.vietnamese_ingested_date) : '',
                chinese_ingested_date: item.chinese_ingested_date ? this.formatIngestedDates(item.chinese_ingested_date) : '',
                chinese_qc_date: item.chinese_qc_date ? this.formatIngestedDates(item.chinese_qc_date) : '',
            }))
            .reverse(); // 최신 데이터가 먼저 표시되도록 역순으로 정렬
        },
        filterOptions() {
            switch (this.selectedTab) {
                case 'Master':
                    return [
                        { text: 'Title', value: 'episode_title' },
                        { text: 'CP', value: 'cp' },
                        { text: 'TA', value: 'english_ta' },
                        { text: 'Category', value: 'category' },
                        { text: 'On Air Date', value: 'on_air_date' },
                        { text: 'English', value: 'english_ingested_date' },
                        { text: 'English CC', value: 'english_cc_ingested_date' },
                        { text: 'Portuguese', value: 'portuguese_ingested_date' },
                        { text: 'Portuguese QC', value: 'portuguese_qc_date' },
                        { text: 'Spanish', value: 'spanish_ingested_date' },
                        { text: 'Spanish QC', value: 'spanish_qc_date' },
                        { text: 'Portuguese AI', value: 'portuguese_ai_ingested_date' },
                        { text: 'Spanish AI', value: 'spanish_ai_ingested_date' },
                        { text: 'French AI', value: 'french_ai_ingested_date' },
                        { text: 'German AI', value: 'german_ai_ingested_date' },
                        { text: 'Italian AI', value: 'italian_ai_ingested_date' },
                        { text: 'Vietnamese', value: 'vietnamese_ingested_date' },
                        { text: 'Chinese', value: 'chinese_ingested_date' },
                        { text: 'Chinese QC', value: 'chinese_qc_date' }
                    ];
                case 'Portuguese':
                    return [
                        { text: 'Title', value: 'episode_title' },
                        { text: 'CP', value: 'cp' },
                        { text: 'TA', value: 'english_ta' },
                        { text: 'Category', value: 'category' },
                        { text: 'On Air Date', value: 'on_air_date' },
                        { text: 'English', value: 'english_ingested_date' },
                        { text: 'Portuguese', value: 'portuguese_ingested_date' },
                        { text: 'Portuguese QC', value: 'portuguese_qc_date' },
                        { text: 'Portuguese AI', value: 'portuguese_ai_ingested_date' }
                    ];
                case 'Spanish':
                    return [
                        { text: 'Title', value: 'episode_title' },
                        { text: 'CP', value: 'cp' },
                        { text: 'TA', value: 'english_ta' },
                        { text: 'Category', value: 'category' },
                        { text: 'On Air Date', value: 'on_air_date' },
                        { text: 'English', value: 'english_ingested_date' },
                        { text: 'Spanish', value: 'spanish_ingested_date' },
                        { text: 'Spanish QC', value: 'spanish_qc_date' },
                        { text: 'Spanish AI', value: 'spanish_ai_ingested_date' }
                    ];
                case 'Vietnamese':
                    return [
                        { text: 'Title', value: 'episode_title' },
                        { text: 'CP', value: 'cp' },
                        { text: 'TA', value: 'english_ta' },
                        { text: 'Category', value: 'category' },
                        { text: 'On Air Date', value: 'on_air_date' },
                        { text: 'English', value: 'english_ingested_date' },
                        { text: 'Vietnamese', value: 'vietnamese_ingested_date' }
                    ];
                case 'Chinese':
                    return [
                        { text: 'Title', value: 'episode_title' },
                        { text: 'CP', value: 'cp' },
                        { text: 'TA', value: 'english_ta' },
                        { text: 'Category', value: 'category' },
                        { text: 'On Air Date', value: 'on_air_date' },
                        { text: 'English', value: 'english_ingested_date' },
                        { text: 'Chinese', value: 'chinese_ingested_date' },
                        { text: 'Chinese QC', value: 'chinese_qc_date' }
                    ];
                case 'French':
                    return [
                        { text: 'Title', value: 'episode_title' },
                        { text: 'CP', value: 'cp' },
                        { text: 'TA', value: 'english_ta' },
                        { text: 'Category', value: 'category' },
                        { text: 'On Air Date', value: 'on_air_date' },
                        { text: 'English', value: 'english_ingested_date' },
                        { text: 'French AI', value: 'french_ai_ingested_date' }
                    ];
                case 'German':
                    return [
                        { text: 'Title', value: 'episode_title' },
                        { text: 'CP', value: 'cp' },
                        { text: 'TA', value: 'english_ta' },
                        { text: 'Category', value: 'category' },
                        { text: 'On Air Date', value: 'on_air_date' },
                        { text: 'English', value: 'english_ingested_date' },
                        { text: 'German AI', value: 'german_ai_ingested_date' }
                    ];
                case 'Italian':
                    return [
                        { text: 'Title', value: 'episode_title' },
                        { text: 'CP', value: 'cp' },
                        { text: 'TA', value: 'english_ta' },
                        { text: 'Category', value: 'category' },
                        { text: 'On Air Date', value: 'on_air_date' },
                        { text: 'English', value: 'english_ingested_date' },
                        { text: 'Italian AI', value: 'italian_ai_ingested_date' }
                    ];
                case 'Old':
                    return [
                        { text: 'Title', value: 'episode_title' },
                        { text: 'Portuguese', value: 'portuguese_ingested_date' },
                        { text: 'Portuguese QC', value: 'portuguese_qc_date' },
                        { text: 'Spanish', value: 'spanish_ingested_date' },
                        { text: 'Spanish QC', value: 'spanish_qc_date' }
                    ];
                default:
                    return [];
            }
        },
        filteredTitleData() {
            return this.titleData.filter(item => {
                // 필터가 날짜 필터일 경우
                if (this.selectedFilter.includes('ingested_date') || this.selectedFilter === 'on_air_date' || this.selectedFilter.includes('qc_date')) {
                    return this.filterByDateRange(item);
                }
                
                // 드롭다운 옵션 필터 (CP, TA, Category 등)
                if (this.selectedFilter === 'cp' || this.selectedFilter === 'english_ta' || this.selectedFilter === 'category') {
                    return this.search === '' || item[this.selectedFilter] === this.search;
                }

                // 텍스트 필터 (기본 검색 필터)
                if (this.search.trim() !== '') {
                    const lowerCaseSearch = this.search.toLowerCase();
                    return item[this.selectedFilter]?.toLowerCase().includes(lowerCaseSearch);
                }
                
                // 기본 필터링 없이 전체 반환
                return true;
            });
        }
    },
    methods: {
        updateTab(index) {
            this.selectedTabIndex = index;
            this.selectedTab = this.tabs[index].value;
            this.selectedFilter = this.filterOptions[0]?.value || ''; // 기본 필터를 첫 번째 값으로 설정

            this.updateFilteredData();
        },
        updateFilteredData() {
            this.tabFilteredData = this.getTabFilteredData();
            this.tabFilteredHeaders = this.getTabFilteredHeaders();
        },
        getTabFilteredData() {
            // 'Old' 탭일 경우, 데이터 소스를 this.allData로 설정
            let data = this.selectedTab === 'Old' ? this.allData : this.filteredTitleData;

            if (this.selectedTab === 'Portuguese') {
                data = data.filter(item => item.portuguese_ingested_date || item.portuguese_qc_date || item.portuguese_ai_ingested_date);
            } else if (this.selectedTab === 'Spanish') {
                data = data.filter(item => item.spanish_ingested_date || item.spanish_qc_date || item.spanish_ai_ingested_date);
            } else if (this.selectedTab === 'Vietnamese') {
                data = data.filter(item => item.vietnamese_ingested_date);
            } else if (this.selectedTab === 'Chinese') {
                data = data.filter(item => item.chinese_ingested_date || item.chinese_qc_date);
            } else if (this.selectedTab === 'French') {
                data = data.filter(item => item.french_ai_ingested_date);
            } else if (this.selectedTab === 'German') {
                data = data.filter(item => item.german_ai_ingested_date);
            } else if (this.selectedTab === 'Italian') {
                data = data.filter(item => item.italian_ai_ingested_date);
            } else if (this.selectedTab === 'Old') {
                data = data
                    .filter(item => item.id < 103086426)
                    .map(item => ({
                        ...item,
                        durationMin: item.duration ? this.formatDuration(item.duration) : '',
                        min: item.duration ? Math.ceil(item.duration / 60) : '',
                        on_air_date: item.on_air_date ? this.convertDateToTimezone(item.on_air_date) : '',
                        portuguese_ingested_date: item.portuguese_ingested_date ? this.formatIngestedDates(item.portuguese_ingested_date) : '',
                        portuguese_qc_date: item.portuguese_qc_date ? this.formatIngestedDates(item.portuguese_qc_date) : '',
                        spanish_ingested_date: item.spanish_ingested_date ? this.formatIngestedDates(item.spanish_ingested_date) : '',
                        spanish_qc_date: item.spanish_qc_date ? this.formatIngestedDates(item.spanish_qc_date) : '',
                    }))
                    .filter(item =>
                        item.portuguese_ingested_date ||
                        item.portuguese_qc_date ||
                        item.spanish_ingested_date ||
                        item.spanish_qc_date
                    );

                if (this.selectedFilter.includes('date')) {
                    data = data.filter(item => this.filterByDateRange(item));
                }
            }

            if (this.search.trim() !== '') {
                const lowerCaseSearch = this.search.toLowerCase();

                // 날짜 필터일 경우
                if (this.selectedFilter.includes('ingested_date') || this.selectedFilter === 'on_air_date' || this.selectedFilter.includes('qc_date')) {
                    data = data.filter(item => this.filterByDateRange(item));
                } 
                // 드롭다운 옵션 필터 (CP, TA, Category 등)
                else if (['cp', 'english_ta', 'category'].includes(this.selectedFilter)) {
                    data = data.filter(item => this.search === '' || item[this.selectedFilter] === this.search);
                } 
                // 텍스트 필터 (기본 검색 필터)
                else {
                    data = data.filter(item => {
                        const itemValue = item[this.selectedFilter]?.toString().toLowerCase() || '';
                        return itemValue.includes(lowerCaseSearch);
                    });
                }
            }

            return data;
        },

        getTabFilteredHeaders() {
            const baseHeaders = [
                { text: 'CP', value: 'cp' },
                { text: 'TA', value: 'english_ta' },
                { text: 'Category', value: 'category' },
                { text: 'Title', value: 'episode_title' },
                { text: 'DT (sec)', value: 'duration' },
                { text: 'DT (min)', value: 'durationMin' },
                { text: 'Min', value: 'min' },
                { text: 'On Air Date', value: 'on_air_date' },
                { text: 'English', value: 'english_ingested_date', width: '165px', align: 'center' },
                ...(this.selectedTab === 'Master' ? [
                    { text: 'English CC', value: 'english_cc_ingested_date', width: '165px', align: 'center' }
                ] : [])
            ];

            // 각 탭의 특수 헤더 설정
            if (this.selectedTab === 'Portuguese') {
                return [
                    ...baseHeaders,
                    { text: 'Portuguese', value: 'portuguese_ingested_date', width: '165px', align: 'center' },
                    { text: 'Portuguese QC', value: 'portuguese_qc_date', width: '165px', align: 'center' },
                    { text: 'Portuguese AI', value: 'portuguese_ai_ingested_date', width: '165px', align: 'center' }
                ];
            } else if (this.selectedTab === 'Spanish') {
                return [
                    ...baseHeaders,
                    { text: 'Spanish', value: 'spanish_ingested_date', width: '165px', align: 'center' },
                    { text: 'Spanish QC', value: 'spanish_qc_date', width: '165px', align: 'center' },
                    { text: 'Spanish AI', value: 'spanish_ai_ingested_date', width: '165px', align: 'center' }
                ];
            } else if (this.selectedTab === 'Vietnamese') {
                return [
                    ...baseHeaders,
                    { text: 'Vietnamese', value: 'vietnamese_ingested_date', width: '165px', align: 'center' },
                ];
            } else if (this.selectedTab === 'Chinese') {
                return [
                    ...baseHeaders,
                    { text: 'Chinese', value: 'chinese_ingested_date', width: '165px', align: 'center' },
                    { text: 'Chinese QC', value: 'chinese_qc_date', width: '165px', align: 'center' },
                ];
            } else if (this.selectedTab === 'French') {
                return [
                    ...baseHeaders,
                    { text: 'French AI', value: 'french_ai_ingested_date', width: '165px', align: 'center' },
                ];
            } else if (this.selectedTab === 'German') {
                return [
                    ...baseHeaders,
                    { text: 'German AI', value: 'german_ai_ingested_date', width: '165px', align: 'center' },
                ];
            } else if (this.selectedTab === 'Italian') {
                return [
                    ...baseHeaders,
                    { text: 'Italian AI', value: 'italian_ai_ingested_date', width: '165px', align: 'center' },
                ];
            } else if (this.selectedTab === 'Old') {
                return [
                    { text: 'CP', value: 'cp' },
                    { text: 'TA', value: 'english_ta' },
                    { text: 'Category', value: 'category' },
                    { text: 'Title', value: 'episode_title' },
                    { text: 'DT (sec)', value: 'duration' },
                    { text: 'DT (min)', value: 'durationMin' },
                    { text: 'Min', value: 'min' },
                    { text: 'On Air Date', value: 'on_air_date' },
                    { text: 'Portuguese', value: 'portuguese_ingested_date', width: '165px', align: 'center' },
                    { text: 'Portuguese QC', value: 'portuguese_qc_date', width: '165px', align: 'center' },
                    { text: 'Spanish', value: 'spanish_ingested_date', width: '165px', align: 'center' },
                    { text: 'Spanish QC', value: 'spanish_qc_date', width: '165px', align: 'center' }
                ];
            }
            return this.headers;
        },

        toggleTimezone() {
            this.timezoneIndex = (this.timezoneIndex + 1) % this.timezones.length;
        },
        convertDateToTimezone(kstDateString) {
            // Parse the date as a local (KST) date without applying any additional offset
            const localDate = new Date(kstDateString);

            if (isNaN(localDate.getTime())) return kstDateString; // Return original if invalid date

            // If current timezone is KST, return as-is in the desired format
            if (this.currentTimezone.name === 'KST (UTC+09:00)') {
                return `${localDate.getFullYear()}-${String(localDate.getMonth() + 1).padStart(2, '0')}-${String(localDate.getDate()).padStart(2, '0')} ` +
                    `${String(localDate.getHours()).padStart(2, '0')}:${String(localDate.getMinutes()).padStart(2, '0')}:${String(localDate.getSeconds()).padStart(2, '0')}`;
            }

            // Convert the date to UTC first, then adjust to the selected timezone
            const utcDate = new Date(localDate.getTime() - 9 * 60 * 60 * 1000); // Remove KST offset to get UTC
            const adjustedDate = new Date(utcDate.getTime() + this.currentTimezone.offset); // Apply new timezone offset

            // Format manually to "YYYY-MM-DD HH:mm:ss"
            const year = adjustedDate.getFullYear();
            const month = String(adjustedDate.getMonth() + 1).padStart(2, '0');
            const day = String(adjustedDate.getDate()).padStart(2, '0');
            const hours = String(adjustedDate.getHours()).padStart(2, '0');
            const minutes = String(adjustedDate.getMinutes()).padStart(2, '0');
            const seconds = String(adjustedDate.getSeconds()).padStart(2, '0');

            return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
        },
        // JSON 형태의 `ingested_date`, `qc_date`에 대한 날짜 변환 로직
        formatIngestedDates(jsonString) {
            const dates = JSON.parse(jsonString); // JSON 파싱
            return Object.values(dates)
                .map(dateString => {
                    const date = new Date(dateString); // 원본 날짜 생성

                    // 원본 데이터가 UTC라고 가정하고, KST로 변환
                    const kstDate = new Date(date.getTime() + 9 * 60 * 60 * 1000);

                    // 현재 선택된 타임존으로 변환
                    const timezoneDate = new Date(kstDate.getTime() + this.currentTimezone.offset - 9 * 60 * 60 * 1000);
                    
                    // 변환된 날짜를 원하는 포맷으로 출력
                    const year = timezoneDate.getFullYear();
                    const month = String(timezoneDate.getMonth() + 1).padStart(2, '0');
                    const day = String(timezoneDate.getDate()).padStart(2, '0');
                    const hours = String(timezoneDate.getHours()).padStart(2, '0');
                    const minutes = String(timezoneDate.getMinutes()).padStart(2, '0');
                    const seconds = String(timezoneDate.getSeconds()).padStart(2, '0');

                    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
                })
                .join('\n');
        },

        updatePage(newPage) {
            this.page = newPage;
        },
        formatDuration(seconds) {
            const totalMinutes = Math.floor(seconds / 60);
            const hours = Math.floor(totalMinutes / 60);
            const minutes = totalMinutes % 60;
            const remainingSeconds = seconds % 60;
            return `${hours}:${String(minutes).padStart(2, '0')}:${String(remainingSeconds).padStart(2, '0')}`;
        },
        updateDateRange() {
            if (this.selectedDates.length === 2) {
                const [fromDate, toDate] = this.selectedDates;
                this.dateRangeText = `${fromDate} - ${toDate}`;
                this.updateFilteredData(); // 날짜 범위를 설정할 때 필터링된 데이터를 업데이트
            }
        },
        filterByDateRange(item) {
            if (this.selectedDates.length < 2) return true; // 날짜 범위가 설정되지 않았으면 전체 반환
            const [fromDate, toDate] = this.selectedDates;
            const itemDate = new Date(item[this.selectedFilter]?.split(' ')[0]); // 해당 항목의 날짜
            return itemDate >= new Date(fromDate) && itemDate <= new Date(toDate);
        },
        // search와 dropdown을 업데이트할 때마다 필터링을 다시 적용
        updateSearch() {
            this.updateFilteredData();
        },
        filterByDropdown(data) {
            if (!this.search) return data;
            return data.filter(item => item[this.selectedFilter] === this.search);
        },
        setDropdownOptions() {
            const field = this.selectedFilter;
            this.dropdownOptions = [...new Set(this.data.map(item => item[field]))];
        },
        exportToExcel() {
            let dataToExport = this.tabFilteredData;
            let headersToExport = this.tabFilteredHeaders;

            // 'Old' 탭일 경우에도 tabFilteredData를 사용하여 그대로 내보냄
            if (this.selectedTab === 'Old') {
                dataToExport = this.tabFilteredData.filter(item => 
                    item.portuguese_ingested_date || 
                    item.portuguese_qc_date || 
                    item.spanish_ingested_date || 
                    item.spanish_qc_date
                );

                // 'Old' 탭에서는 특정 헤더를 제외
                headersToExport = headersToExport.filter(header => 
                    header.value !== 'vietnamese_ingested_date' &&
                    header.value !== 'chinese_ingested_date' &&
                    header.value !== 'chinese_qc_date'
                );
            }

            const orderedData = dataToExport.map(item => {
                return headersToExport.reduce((acc, header) => {
                    acc[header.text] = item[header.value] || ''; // 데이터가 없는 경우 빈 문자열
                    return acc;
                }, {});
            });

            const now = new Date();
            const timestamp = now.getFullYear() + "-" + 
                                String(now.getMonth() + 1).padStart(2, '0') + "-" + 
                                String(now.getDate()).padStart(2, '0') + "_" +
                                String(now.getHours()).padStart(2, '0') + ":" + 
                                String(now.getMinutes()).padStart(2, '0') + ":" + 
                                String(now.getSeconds()).padStart(2, '0');

            const filename = `subtitle_data_${timestamp}.xlsx`;

            const ws = XLSX.utils.json_to_sheet(orderedData, { header: headersToExport.map(h => h.text) });
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, 'Subtitle Data');
            XLSX.writeFile(wb, filename);
        }


    },
    mounted() {
        axios.get('/api/data') // Fetch data from API
            .then(response => {
                this.allData = response.data; // 원본 데이터 저장
                this.data = this.allData.filter(item => item.id >= 103086426); // Master 기본 필터링
                this.loading = false;
                this.updateFilteredData(); // 초기 데이터를 로드한 후 필터링
            })
            .catch(error => {
                console.error('Error fetching data:', error);
                this.loading = false;
            });
    }
};
</script>

<style scoped>

/* Table 전체 가로 스크롤 설정 */
.scrollable-table {
    overflow-x: auto;
    width: 100%;
}

/* Vuetify에서 고정 열 스타일 */
.custom-table .v-data-table__wrapper {
    overflow-x: auto !important;
}

.custom-table td,
.custom-table th {
    white-space: nowrap; /* 텍스트 줄바꿈 방지 */
}

::v-deep .v-data-table td,
::v-deep .v-data-table th {
    padding: 0; /* 기본 padding 제거 */
    white-space: nowrap; /* 텍스트 줄바꿈 방지 */
}

/* 고정 열 스타일 적용 */
::v-deep .v-data-table td:nth-child(1),
::v-deep .v-data-table th:nth-child(1),
::v-deep .v-data-table td:nth-child(2),
::v-deep .v-data-table th:nth-child(2),
::v-deep .v-data-table td:nth-child(3),
::v-deep .v-data-table th:nth-child(3),
::v-deep .v-data-table td:nth-child(4),
::v-deep .v-data-table th:nth-child(4),
::v-deep .v-data-table td:nth-child(5),
::v-deep .v-data-table th:nth-child(5),
::v-deep .v-data-table td:nth-child(6),
::v-deep .v-data-table th:nth-child(6),
::v-deep .v-data-table td:nth-child(7),
::v-deep .v-data-table th:nth-child(7),
::v-deep .v-data-table td:nth-child(8),
::v-deep .v-data-table th:nth-child(8) {
    position: sticky;
    left: 0;
    background-color: white;
    z-index: 1;
}

::v-deep .v-data-table tbody tr:hover td:nth-child(-n+8),
::v-deep .v-data-table tbody tr:hover th:nth-child(-n+8) {
    background-color: #e5eef9 !important;
}

/* 각각의 고정된 열에 대한 left 위치 조정 */
::v-deep .v-data-table td:nth-child(1),
::v-deep .v-data-table th:nth-child(1) { 
    left: 0px; /* Title 열 위치 고정 */
    z-index: 3;
    min-width: 100px !important;
    max-width: 100px !important;
 }
::v-deep .v-data-table td:nth-child(2),
::v-deep .v-data-table th:nth-child(2) { 
    left: 100px; /* Title 열 위치 고정 */
    z-index: 3;
    min-width: 80px !important;
    max-width: 80px !important;
 }
::v-deep .v-data-table td:nth-child(3),
::v-deep .v-data-table th:nth-child(3) { 
    left: 180px; /* Title 열 위치 고정 */
    z-index: 3;
    min-width: 80px !important;
    max-width: 80px !important;
 }
::v-deep .v-data-table td:nth-child(4),
::v-deep .v-data-table th:nth-child(4) {
    left: 260px; /* Title 열 위치 고정 */
    z-index: 3;
    min-width: 300px !important;
    max-width: 300px !important;
}

/* Title 열 뒤부터 스크롤이 겹치지 않도록 설정 */
::v-deep .v-data-table td:nth-child(5),
::v-deep .v-data-table th:nth-child(5) {
    left: 560px; /* Title 끝 위치에 맞춰 배치 */
    z-index: 3;
    min-width: 70px !important;
    max-width: 70px !important;
}

/* Duration 및 이후 열 설정 */
::v-deep .v-data-table td:nth-child(6),
::v-deep .v-data-table th:nth-child(6) {
    left: 630px; /* Title 끝 위치에 맞춰 배치 */
    z-index: 3;
    min-width: 90px !important;
    max-width: 90px !important;
}
::v-deep .v-data-table td:nth-child(7),
::v-deep .v-data-table th:nth-child(7) {
    left: 720px; /* Title 끝 위치에 맞춰 배치 */
    z-index: 3;
    min-width: 70px !important;
    max-width: 70px !important;
}
::v-deep .v-data-table td:nth-child(8),
::v-deep .v-data-table th:nth-child(8) {
    left: 790px; /* Title 끝 위치에 맞춰 배치 */
    z-index: 3;
    min-width: 165px !important;
    max-width: 165px !important;
}

.v-data-table-header,
::v-deep .v-data-table-header th {
    position: sticky;
    background-color: #ededed !important;
    color: rgb(99, 99, 99) !important;
    text-align: center  !important; /* 좌우 중간 정렬 */
    vertical-align: middle !important; /* 상하 중간 정렬 */
    white-space: nowrap !important; /* 텍스트 줄바꿈 방지 */
    padding: 10px;
    border-left: 1px solid #ededed; /* 헤더 사이의 빈틈 제거 */
}

::v-deep .v-data-table__wrapper {
    overflow-x: auto !important; /* Always show horizontal scroll if needed */
    margin-top: 45px;
    min-width: 800px; /* Adjust this to a value that fits your design */
}

::v-deep .v-data-table__wrapper td {
    white-space: pre-line !important; /* Prevent text wrapping */
}

::v-deep .v-data-table-header th:hover {
  color: black !important;
}

::v-deep .v-data-table tbody tr:hover {
  background-color: #e5eef9 !important;
}

.filter-select {
    margin: 30px 20px 30px 10px;
}

::v-deep .v-toolbar__content {
    margin-top: 30px !important;
    margin-bottom: 15px;
}
</style>
